// import React from 'react';
import { SxStyle } from '../../../../types';
import Box from '@mui/material/Box';
import { useStringsContext } from '../../../../contexts/strings';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderClassNamePropType,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { SquadPlayer } from '../types';
import IconButton from '@mui/material/IconButton';
import { CircularProgress, Typography } from '@mui/material';
import { translateCountry } from '../../../../countries';
import PlayerAvatar from '../../../common/player-avatar';
import { useAllTeams } from '../../../../hooks/team';
import { capitalize } from 'lodash';
import { getLeagueImage } from '../utils';

const styles: SxStyle = {
  dataGridContainer: {
    width: '100%',
    height: 'calc(100% - 80px)',
    marginTop: 4,
  },
  dataGridHeader: {
    '& > .MuiDataGrid-columnSeparator--sideRight': {
      visibility: 'hidden',
    },
  },
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
  textEllipse: {
    textOverflow: 'ellipsis', // TODO check double ellipse
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  link: {
    cursor: 'pointer',
  },
  healthDescription: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};

interface Props {
  players: SquadPlayer[] | null;
  loading: boolean;
  onEditPlayer: (player: SquadPlayer) => void;
  onStatsPlayer: (player: SquadPlayer) => void;
  onDeletePlayer: (player: SquadPlayer) => void;
}

interface ListAvatarProps {
  params: GridRenderCellParams;
  onClick: (player: SquadPlayer) => void;
}

interface EditButtonsProps
  extends Pick<Props, 'onEditPlayer' | 'onStatsPlayer' | 'onDeletePlayer'> {
  params: GridRenderCellParams;
}

interface ListPositionProps {
  params: GridRenderCellParams;
}

interface ListStatusProps {
  params: GridRenderCellParams;
}

interface ShirtNumberProps {
  params: GridRenderCellParams;
}

interface IdNumberProps {
  params: GridRenderCellParams;
  type: 'smcId' | 'fogisId' | 'optaId';
}

interface ListNationalityProps {
  params: GridRenderCellParams;
}

const ListAvatar = ({ params, onClick }: ListAvatarProps) => {
  const player: SquadPlayer = params.row;

  return (
    <Stack
      onClick={() => onClick(player)}
      sx={styles.link}
      spacing={4}
      direction="row"
      alignItems="center"
    >
      <PlayerAvatar
        srcImage={player.images?.sefImagePng}
        fullName={player.displayName || player.fullName}
      />
      <Typography variant="body2">{player.displayName || player.fullName}</Typography>
    </Stack>
  );
};

const ListEditButtons = ({
  params,
  onEditPlayer,
  onStatsPlayer,
  onDeletePlayer,
}: EditButtonsProps) => (
  <Stack spacing={1} direction="row" alignItems="center">
    <IconButton onClick={() => onEditPlayer(params.row)}>
      <InfoIcon color="primary" />
    </IconButton>
    <IconButton onClick={() => onStatsPlayer(params.row)}>
      <AnalyticsIcon color="primary" />
    </IconButton>
    <IconButton onClick={() => onDeletePlayer(params.row)}>
      <DeleteIcon color="error" />
    </IconButton>
  </Stack>
);

const ListPosition = ({ params }: ListPositionProps) => {
  const { strings } = useStringsContext();
  const player: SquadPlayer = params.row;
  const primary = player.position?.primary;
  let positions = primary ? [primary] : [];
  positions = player.position?.other ? [...positions, ...player.position.other] : positions;
  const value = positions.map((position) => strings.squadPlayerPositions[position]).join(' / ');

  return params.row.position.primary ? (
    <Box sx={styles.textEllipse}>{value}</Box>
  ) : (
    <Typography variant="italic">{strings.squad.missingInformation}</Typography>
  );
};

const ListStatus = ({ params }: ListStatusProps) => {
  const { strings } = useStringsContext();
  const player: SquadPlayer = params.row;
  const currentClub = player.activeCurrentClub;

  return currentClub?.status ? <>{strings.squadPlayer[currentClub.status]}</> : <></>;
};

const ShirtNumber = ({ params }: ShirtNumberProps) => {
  const player: SquadPlayer = params.row;

  return <>{player.activeCurrentClub?.shirtNumber || ''}</>;
};

const IdNumber = ({ params, type }: IdNumberProps) => {
  const player: SquadPlayer = params.row;

  return <>{player[type] || ''}</>;
};

const ListNationality = ({ params }: ListNationalityProps) => {
  const { strings } = useStringsContext();
  const player: SquadPlayer = params.row;

  return player.nationality ? (
    <>{translateCountry(player.nationality)}</>
  ) : (
    <Typography variant="italic">{strings.squad.missingInformation}</Typography>
  );
};

const ListTeam = ({ params }: { params: GridRenderCellParams }) => {
  const team = params.row.activeCurrentClub;
  const { data } = useAllTeams();
  const { logoImageUrl } = (data || []).find((t) => t.id === team.id) || {};

  return (
    <Stack spacing={4} direction="row" alignItems="center">
      <img height={40} src={logoImageUrl} alt={team.name} title={team.name} />
      <Typography variant="body2">{team.name}</Typography>
    </Stack>
  );
};

const ListLeague = ({ params }: { params: GridRenderCellParams }) => {
  const team = params.row.activeCurrentClub;
  const image = getLeagueImage(team.league);

  const leagueName = (() => {
    if (!team.league || team.league === 'none') {
      return '';
    }

    return capitalize(team.league);
  })();

  return (
    <Stack spacing={4} direction="row" alignItems="center">
      {image ? <img height={40} src={image} alt={leagueName} title={leagueName} /> : null}
      <Typography variant="body2">{leagueName}</Typography>
    </Stack>
  );
};

const PlayerList = ({ players, loading, onEditPlayer, onStatsPlayer, onDeletePlayer }: Props) => {
  const { strings } = useStringsContext();

  const defaultColumnOptions = {
    headerClassName: styles.dataGridHeader as GridColumnHeaderClassNamePropType, // TODO Check
    maxWidth: 300,
  };

  const columns: GridColDef[] = [
    {
      field: 'league',
      headerName: strings.appConfig.league,
      ...defaultColumnOptions,
      minWidth: 250,
      valueGetter: (value, row) => row.activeCurrentClub.league,
      renderCell: (params: GridRenderCellParams) => <ListLeague params={params} />,
    },
    {
      field: 'team',
      headerName: strings.squad.squad,
      ...defaultColumnOptions,
      minWidth: 250,
      valueGetter: (value, row) => row.activeCurrentClub.name,
      renderCell: (params: GridRenderCellParams) => <ListTeam params={params} />,
    },
    {
      field: 'fullName',
      headerName: strings.squad.player,
      ...defaultColumnOptions,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <ListAvatar onClick={onEditPlayer} params={params} />
      ),
    },
    {
      field: 'number',
      headerName: strings.squadPlayer.shirtNumber,
      ...defaultColumnOptions,
      renderCell: (params: GridRenderCellParams) => <ShirtNumber params={params} />,
      valueGetter: (value, row) => row.activeCurrentClub.shirtNumber,
    },
    {
      field: 'position',
      headerName: strings.squadPlayer.position,
      ...defaultColumnOptions,
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <ListPosition params={params} />,
      valueGetter: (value, row) => row.position.primary,
    },
    {
      field: 'nationality',
      headerName: strings.squadPlayer.nationality,
      ...defaultColumnOptions,
      minWidth: 150,
      maxWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <ListNationality params={params} />,
    },
    {
      field: 'status',
      headerName: strings.squad.status,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <ListStatus params={params} />,
      valueGetter: (value, row) => row.activeCurrentClub.status,
    },
    {
      field: 'smcId',
      headerName: strings.squadPlayer.smcId,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <IdNumber params={params} type={'smcId'} />,
    },
    {
      field: 'fogisId',
      headerName: strings.squadPlayer.fogisId,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <IdNumber params={params} type={'fogisId'} />,
    },
    {
      field: 'optaId',
      headerName: strings.squadPlayer.optaId,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <IdNumber params={params} type={'optaId'} />,
    },
    {
      field: 'clId',
      headerName: '',
      headerClassName: styles.dataGridHeader as GridColumnHeaderClassNamePropType, // TODO Check
      minWidth: 200,
      flex: 1,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <ListEditButtons
          params={params}
          onEditPlayer={onEditPlayer}
          onStatsPlayer={onStatsPlayer}
          onDeletePlayer={onDeletePlayer}
        />
      ),
    },
  ];

  return (
    <Paper elevation={2} sx={styles.dataGridContainer}>
      <DataGrid
        sx={styles.dataGrid}
        rows={players || []}
        columns={columns}
        getRowId={(row) => row.fogisId + row.activeCurrentClub.id}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'fullName',
                sort: 'asc',
              },
            ],
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                  <CircularProgress />
                </Box>
              ) : null}
            </>
          ),
        }}
      />
    </Paper>
  );
};

export default PlayerList;
